import { Article } from '../types'

export function relativeTime(iso: string | null): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const diffMs = Date.now() - then
  const day = 86_400_000
  const hour = 3_600_000
  const min = 60_000
  // floor, not round: rounding overshoots its own unit at the boundary, so
  // 59.98 minutes rendered as "60 minutes ago" and 23.9 hours as "24 hours ago"
  // instead of rolling over. floor also matches the usual convention, where
  // 90 minutes reads "1 hour ago" until the second hour actually elapses.
  if (diffMs < hour) {
    const n = Math.max(1, Math.floor(diffMs / min))
    return `${n} minute${n === 1 ? '' : 's'} ago`
  }
  if (diffMs < day) {
    const n = Math.floor(diffMs / hour)
    return `${n} hour${n === 1 ? '' : 's'} ago`
  }
  const n = Math.floor(diffMs / day)
  return `${n} day${n === 1 ? '' : 's'} ago`
}

/**
 * Returns the display site name for an article.
 *
 * The URL fallback is guarded: `new URL()` throws on a malformed stored value,
 * and an unguarded throw here propagates out of ArticleCard's render and blanks
 * the whole list rather than degrading the one bad row.
 */
export function getSiteName(article: Article): string {
  if (article.source === 'email') return 'via email'
  if (article.siteName) return article.siteName
  if (article.domain) return article.domain
  if (!article.url) return ''
  try {
    // Anchored: an unanchored 'www.' matches anywhere, turning the host
    // 'nwww.example.com' into 'nexample.com'.
    return new URL(article.url).hostname.replace(/^www\./, '')
  } catch {
    return ''
  }
}

/** Returns the best available display title for an article. */
export function getArticleTitle(article: Article): string {
  return article.title || article.url || '(untitled)'
}

/**
 * Returns true if a content string appears to be HTML.
 *
 * Heuristic: if the trimmed content starts with '<', treat it as HTML.
 * Email bodies from SES are stored as raw HTML; plain-text articles extracted
 * from the web never start with '<' in our pipeline. This could theoretically
 * produce a false positive for plain text that legitimately starts with '<',
 * but that case does not occur with the current extraction pipeline.
 */
export function isHtmlContent(content: string): boolean {
  return /^</.test(content.trimStart())
}
